import { FC, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import { DateTime } from "luxon";
import { Brand, BrandAdToken, BrandAsset, BrandAssetKind, FontVariant } from "../../../shared/models/brand.model";
import Loader from "../../../components/common/loader/loader";
import { storageKey } from "../../../shared/models/brandVariants";
import { BRAND_IDENTITY_V2, useFeatureFlag } from "../../../shared/featureFlags/useFeatureFlag";
import type { BrandAdTokenValueEntry, BrandColorEntry, BrandSectionField, BrandTypographyStyleEntry } from "../hooks/useOrganizationBrand";
import type { BrandAssetEntry } from "../hooks/brandAssetEntries";
import { brandRequiredValues, missingRequiredFields } from "../hooks/brandRequiredFields";
import BrandAssetSection from "./BrandAssetSection";
import BrandColorsSection from "./BrandColorsSection";
import BrandTypographySection from "./BrandTypographySection";
import BrandAdTokenValuesSection from "./BrandAdTokenValuesSection";
import useBrandDateFormats from "../hooks/useBrandDateFormats";
import BrandAssetPreview, { baseName, prefersDarkCanvas } from "./BrandAssetPreview";
import "./brandTab.css";

interface Props {
  brandForm: Brand;
  setBrandForm: any;
  update: () => Promise<void>;
  /** The brand as last loaded or saved; a fresh one is when an empty Brand Name is prefilled. */
  savedBrand: Brand;
  onSaveSection: (fields: BrandSectionField[]) => Promise<void>;
  organizationName: string;
  /** The Colors section behind brand-identity-v2: the list as edited, and as last saved. */
  colors: BrandColorEntry[];
  savedColors: BrandColorEntry[];
  setColors: (update: (prev: BrandColorEntry[]) => BrandColorEntry[]) => void;
  onLoadColors: () => Promise<void>;
  onSaveColors: () => Promise<void>;
  /** The Typography section behind brand-identity-v2: the styles as edited. */
  typographyStyles: BrandTypographyStyleEntry[];
  setTypographyStyles: (update: (prev: BrandTypographyStyleEntry[]) => BrandTypographyStyleEntry[]) => void;
  onLoadTypographyStyles: () => Promise<void>;
  onSaveTypographyStyles: () => Promise<void>;
  /** The Ad Token Values section behind brand-identity-v2: the values as edited, and what's left to add. */
  adTokenValues: BrandAdTokenValueEntry[];
  setAdTokenValues: (update: (prev: BrandAdTokenValueEntry[]) => BrandAdTokenValueEntry[]) => void;
  availableAdTokens: BrandAdToken[];
  onLoadAdTokenValues: () => Promise<void>;
  onSaveAdTokenValues: () => Promise<void>;
  /** The asset sections behind brand-identity-v2: every asset as edited, saved one kind at a time. */
  assets: BrandAssetEntry[];
  setAssets: (update: (prev: BrandAssetEntry[]) => BrandAssetEntry[]) => void;
  onSaveAssets: (kind: BrandAssetKind) => Promise<void>;
  isLoading: boolean;
  onUploadFont: (file: File) => Promise<void>;
  onUnlinkFont: (fontId: number) => Promise<void>;
  onSetDefaultLogo: (assetId: number) => Promise<void>;
  onAddAsset: (file: File, makeDefault?: boolean) => Promise<void>;
  onReplaceAsset: (assetId: number, file: File) => Promise<void>;
  onSetAssetTag: (assetId: number, tag: string | null) => Promise<void>;
  isFontUploading: boolean;
}

interface LogoTile {
  key: string;
  label: string;
  tag: string | null;
  fileLabel: string;
  url: string;
  assetId: number | null;
  isDefault: boolean;
  width: number | null;
  height: number | null;
  startDark: boolean;
}

const BRAND_FIELDS: BrandSectionField[] = ["display_name", "company_site_url", "guidelines_url", "demographics"];
const EDITORIAL_FIELDS: BrandSectionField[] = [
  "capitalization",
  "oxford_comma",
  "em_dash_spaces",
  "ampersand_for_and",
  "key_terms",
  "brand_notes",
  "preferred_date_format",
];

// Yes/No answers stay tri-state: null means nobody has said.
const YES_NO_FIELDS = [
  { field: "oxford_comma", label: "Oxford comma" },
  { field: "em_dash_spaces", label: "Space around em dashes" },
  { field: "ampersand_for_and", label: 'Ampersand instead of "and"' },
] as const;

// The sections behind brand-identity-v2, in page order: the Brand Identity mockup's order
// (MPN-2173), Logos and Colors first. Each asset kind is a section of its own; Typography
// follows the Fonts it can draw on; Ad Token Values isn't in the mockup, so it comes last.
const V2_SECTIONS = [
  "BRAND",
  "LOGO",
  "COLORS",
  "FAVICON",
  "ICON",
  "FONTS",
  "TYPOGRAPHY",
  "GRAPHIC_PATTERN",
  "LIBRARY",
  "EDITORIAL",
  "AD_TOKEN_VALUES",
] as const;

type V2Section = (typeof V2_SECTIONS)[number];

const ACCEPTED_FONT_TYPES = ".ttf,.otf,.woff,.woff2";

const COLOR_ROLES = [
  { field: "primary_color_code", label: "Primary" },
  { field: "secondary_color_code", label: "Secondary" },
  { field: "tertiary_color_code", label: "Tertiary" },
] as const;

type ColorField = (typeof COLOR_ROLES)[number]["field"];

const FONT_WEIGHTS: Record<number, string> = {
  100: "Thin",
  200: "Extra Light",
  300: "Light",
  400: "Regular",
  500: "Medium",
  600: "Semi Bold",
  700: "Bold",
  800: "Extra Bold",
  900: "Black",
};

const orientationOf = (width: number | null, height: number | null) => {
  if (!width || !height) return null;
  const ratio = width / height;
  if (ratio > 1.25) return "Horizontal";
  if (ratio < 0.8) return "Vertical";
  return "Square";
};

const assetTile = (asset: BrandAsset, defaultKey: string): LogoTile => ({
  key: `asset-${asset.id}`,
  label: asset.tag ?? "Untagged",
  tag: asset.tag,
  fileLabel: asset.description || baseName(asset.name),
  url: asset.url ?? "",
  assetId: asset.id,
  isDefault: !!defaultKey && storageKey(asset.url) === defaultKey,
  width: asset.width,
  height: asset.height,
  startDark: prefersDarkCanvas(asset.tag, asset.name),
});

const OrganizationBrandTab: FC<Props> = ({
  brandForm,
  setBrandForm,
  update,
  savedBrand,
  onSaveSection,
  organizationName,
  colors,
  savedColors,
  setColors,
  onLoadColors,
  onSaveColors,
  typographyStyles,
  setTypographyStyles,
  onLoadTypographyStyles,
  onSaveTypographyStyles,
  adTokenValues,
  setAdTokenValues,
  availableAdTokens,
  onLoadAdTokenValues,
  onSaveAdTokenValues,
  assets,
  setAssets,
  onSaveAssets,
  isLoading,
  onUploadFont,
  onUnlinkFont,
  onSetDefaultLogo,
  onAddAsset,
  onReplaceAsset,
  onSetAssetTag,
  isFontUploading,
}) => {
  const fontInputRef = useRef<HTMLInputElement>(null);
  const [naturalSizes, setNaturalSizes] = useState<Record<string, { width: number; height: number }>>({});
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [customTag, setCustomTag] = useState<string>("");
  const brandIdentityV2 = useFeatureFlag(BRAND_IDENTITY_V2);
  const dateFormats = useBrandDateFormats(brandIdentityV2);
  // What the saved Brand still needs (MPN-2174). Saves aren't blocked by it; the banner says so.
  const missingFields = savedBrand.id ? missingRequiredFields(brandRequiredValues(savedBrand)) : [];

  // An empty Brand Name opens as the Organization's name. Only the input changes; the
  // column is written when someone saves the section. Once per brand opened, not after a
  // save, so a name someone cleared on purpose stays cleared.
  useEffect(() => {
    if (!brandIdentityV2 || !savedBrand.id || savedBrand.display_name || !organizationName) return;
    setBrandForm((prev: Brand) => (prev.display_name ? prev : { ...prev, display_name: organizationName }));
  }, [brandIdentityV2, savedBrand.id, organizationName]);

  // The color list, typography styles and ad token values have their own endpoints, read only once the page is on them.
  useEffect(() => {
    if (!brandIdentityV2 || !savedBrand.id) return;
    void onLoadColors();
    void onLoadTypographyStyles();
    void onLoadAdTokenValues();
  }, [brandIdentityV2, savedBrand.id]);

  const setField = (field: keyof Brand, value: string | boolean | null) => {
    setBrandForm((prev: Brand) => ({ ...prev, [field]: value }));
  };

  const yesNo = (value: boolean | null) => (value === null ? "" : String(value));

  // Each date format is offered as today's date written that way. The list comes from the server.
  const today = DateTime.now().setLocale("en-US");
  const defaultDateFormat = dateFormats.formats.find((format) => format.is_default);
  const savedDateFormat = brandForm.preferred_date_format;
  // A code the list doesn't have (it failed to load) is still shown as what's saved.
  const unlistedDateFormat =
    savedDateFormat && !dateFormats.formats.some((format) => format.code === savedDateFormat) ? savedDateFormat : null;

  const tiles = useMemo<LogoTile[]>(() => {
    const defaultKey = storageKey(brandForm.logo_url);
    const assets = (brandForm.assets ?? []).map((asset) => assetTile(asset, defaultKey));
    const ordered = [...assets.filter((t) => t.isDefault), ...assets.filter((t) => !t.isDefault)];

    if (brandForm.logo_url && !assets.some((t) => t.isDefault)) {
      ordered.unshift({
        key: "brand-logo-url",
        label: "Uploaded logo",
        tag: null,
        fileLabel: baseName(defaultKey),
        url: brandForm.logo_url,
        assetId: null,
        isDefault: true,
        width: null,
        height: null,
        startDark: false,
      });
    }


    return ordered;
  }, [brandForm.assets, brandForm.logo_url]);

  const setColor = (field: string, value: string) => {
    setBrandForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleFontUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUploadFont(file);
    }
    if (fontInputRef.current) {
      fontInputRef.current.value = "";
    }
  };

  const formatWeight = (font: FontVariant) => {
    const label = FONT_WEIGHTS[font.weight] || `Weight ${font.weight}`;
    return font.italic ? `${label} Italic` : label;
  };

  // Reassigning a role swaps the two colours rather than overwriting one, so no
  // hex is lost moving a colour up or down the order.
  const assignRole = (fromField: ColorField, toField: ColorField) => {
    if (fromField === toField) return;
    setBrandForm((prev: any) => ({
      ...prev,
      [toField]: prev[fromField],
      [fromField]: prev[toField],
    }));
  };

  const handleAssetFile = (e: React.ChangeEvent<HTMLInputElement>, tile?: LogoTile) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!tile) return void onAddAsset(file);
    // A legacy logo_url with no asset row behind it can't be replaced in place —
    // upload it as a real asset and promote it, which heals the unbacked default.
    if (tile.assetId === null) return void onAddAsset(file, true);
    void onReplaceAsset(tile.assetId, file);
  };

  const commitTag = (tile: LogoTile) => {
    setEditingTag(null);
    const value = customTag.trim();
    if (value !== (tile.tag ?? "")) void onSetAssetTag(tile.assetId as number, value || null);
  };

  const renderTile = (tile: LogoTile) => {
    const size = naturalSizes[tile.key] ?? { width: tile.width, height: tile.height };
    const orientation = orientationOf(size.width, size.height);
    const selectable = tile.assetId !== null && !tile.isDefault;
    const dimensions = size.width && size.height ? `${size.width}×${size.height}` : "";

    return (
      <div
        key={tile.key}
        className={`brand-tile ${tile.isDefault ? "brand-tile-default" : ""}`}
      >
        <BrandAssetPreview
          url={tile.url}
          label={tile.label}
          startDark={tile.startDark}
          onNaturalSize={(width, height) =>
            setNaturalSizes((prev) =>
              prev[tile.key]?.width === width && prev[tile.key]?.height === height
                ? prev
                : { ...prev, [tile.key]: { width, height } }
            )
          }
        />

        <div className="brand-tile-body">
          {editingTag === tile.key ? (
            <Form.Control
              autoFocus
              className="brand-tag-input"
              value={customTag}
              placeholder="Untagged"
              onChange={(e) => setCustomTag(e.target.value)}
              onBlur={() => commitTag(tile)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitTag(tile);
                if (e.key === "Escape") setEditingTag(null);
              }}
            />
          ) : tile.assetId !== null ? (
            <button
              type="button"
              className="brand-tile-name brand-tile-name-editable"
              title={`${tile.label} — click to edit`}
              onClick={() => {
                setEditingTag(tile.key);
                setCustomTag(tile.tag ?? "");
              }}
            >
              {tile.label}
              <i className="bi bi-pencil ms-1"></i>
            </button>
          ) : (
            <span className="brand-tile-name" title={tile.label}>
              {tile.label}
            </span>
          )}
          <div className="d-flex align-items-center gap-1 mt-1 mb-1 flex-wrap">
            {tile.isDefault && <span className="brand-chip">Default</span>}
            {orientation && <span className="brand-chip brand-chip-muted">{orientation}</span>}
          </div>
          <div className="brand-tile-meta" title={tile.fileLabel}>
            {tile.fileLabel || "\u00a0"}
          </div>
          {dimensions && <div className="brand-tile-meta">{dimensions}</div>}
        </div>

        <div className="brand-tile-actions">
          <input
            type="file"
            id={`replace-${tile.key}`}
            accept="image/*"
            className="d-none"
            onChange={(e) => handleAssetFile(e, tile)}
          />
          <label
            htmlFor={`replace-${tile.key}`}
            className={`brand-tile-select ${isLoading ? "is-busy" : ""}`}
            title={`Replace the ${tile.label} image`}
          >
            Replace
          </label>
          {selectable && (
            <button
              type="button"
              className="brand-tile-select"
              disabled={isLoading}
              aria-label={`Set ${tile.label} as the default logo`}
              onClick={() => onSetDefaultLogo(tile.assetId as number)}
            >
              Set as default
            </button>
          )}
        </div>
      </div>
    );
  };

  const fontsSection = (
    <div className="brand-section">
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h6 className="brand-section-title">Fonts</h6>
          <p className="brand-section-hint">Available to text layers in Create.</p>
        </div>
        <div className="d-flex align-items-center gap-3">
          <span className="brand-section-count">
            {brandForm.fonts.length} {brandForm.fonts.length === 1 ? "font" : "fonts"}
          </span>
          <input
            ref={fontInputRef}
            type="file"
            id="font-upload"
            accept={ACCEPTED_FONT_TYPES}
            className="d-none"
            onChange={handleFontUpload}
          />
          <label
            htmlFor="font-upload"
            className={`btn btn-sm btn-outline-primary mb-0 text-nowrap ${isFontUploading ? "disabled" : ""}`}
          >
            {isFontUploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Uploading...
              </>
            ) : (
              <>
                <i className="bi bi-upload me-2"></i>
                Upload Font
              </>
            )}
          </label>
        </div>
      </div>

      {brandForm.fonts.length > 0 && (
        <div className="mb-3">
          {brandForm.fonts.map((font) => (
            <div
              key={font.id}
              className="brand-font-row d-flex align-items-center justify-content-between px-3 py-2 mb-2"
            >
              <div>
                <span className="fw-semibold">{font.family_name}</span>
                <span className="text-muted ms-2 small">
                  {formatWeight(font)} &middot; {font.format.toUpperCase()}
                  {font.source !== "upload" && ` · ${font.source}`}
                </span>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => onUnlinkFont(font.id)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="text-muted small">Supported formats: TTF, OTF, WOFF, WOFF2</div>
    </div>
  );

  const assetSection = (kind: BrandAssetKind) => (
    <BrandAssetSection
      kind={kind}
      assets={assets}
      savedAssets={savedBrand.assets ?? []}
      setAssets={setAssets}
      onSave={() => onSaveAssets(kind)}
      onReplace={onReplaceAsset}
      onSetDefault={onSetDefaultLogo}
      isLoading={isLoading}
    />
  );

  const v2Sections: Record<V2Section, ReactNode> = {
    BRAND: (
      <div className="brand-section">
        <h6 className="brand-section-title">Brand</h6>
        <p className="brand-section-hint">How the brand is named and where its guidelines live.</p>
        <Row className="mt-3">
          <Col md={6}>
            <Form.Group className="mb-3" controlId="brand-display-name">
              <Form.Label className="fw-semibold">Brand Name</Form.Label>
              <Form.Control
                type="text"
                value={brandForm.display_name}
                onChange={(e) => setField("display_name", e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="brand-site">
              <Form.Label className="fw-semibold">Brand Site</Form.Label>
              <Form.Control
                type="url"
                value={brandForm.company_site_url}
                onChange={(e) => setField("company_site_url", e.target.value)}
                placeholder="https://company.com"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="brand-guidelines-url">
              <Form.Label className="fw-semibold">Brand Guidelines URL</Form.Label>
              <Form.Control
                type="url"
                value={brandForm.guidelines_url}
                onChange={(e) => setField("guidelines_url", e.target.value)}
                placeholder="https://company.com/brand"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="brand-demographics">
              <Form.Label className="fw-semibold">Demographics</Form.Label>
              <Form.Control
                type="text"
                value={brandForm.demographics}
                onChange={(e) => setField("demographics", e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <div className="d-flex justify-content-end">
          <Button variant="primary" size="sm" onClick={() => onSaveSection(BRAND_FIELDS)} disabled={isLoading}>
            Save Brand
          </Button>
        </div>
      </div>
    ),
    LOGO: assetSection("LOGO"),
    COLORS: (
      <BrandColorsSection
        colors={colors}
        savedColors={savedColors}
        setColors={setColors}
        onSave={onSaveColors}
        isLoading={isLoading}
      />
    ),
    FAVICON: assetSection("FAVICON"),
    ICON: assetSection("ICON"),
    FONTS: fontsSection,
    TYPOGRAPHY: (
      <BrandTypographySection
        styles={typographyStyles}
        setStyles={setTypographyStyles}
        fonts={brandForm.fonts}
        onSave={onSaveTypographyStyles}
        isLoading={isLoading}
      />
    ),
    GRAPHIC_PATTERN: assetSection("GRAPHIC_PATTERN"),
    LIBRARY: assetSection("LIBRARY"),
    EDITORIAL: (
      <div className="brand-section">
        <h6 className="brand-section-title">Editorial Guidelines</h6>
        <p className="brand-section-hint">The house style for copy written for this brand.</p>
        <Row className="mt-3">
          <Col md={6}>
            <Form.Group className="mb-3" controlId="brand-capitalization">
              <Form.Label className="fw-semibold">Capitalization</Form.Label>
              <Form.Select
                value={brandForm.capitalization}
                onChange={(e) => setField("capitalization", e.target.value)}
              >
                <option value="">Not set</option>
                <option value="TITLE_CASE">Title Case</option>
                <option value="SENTENCE_CASE">Sentence Case</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="brand-preferred-date-format">
              <Form.Label className="fw-semibold">Preferred Date Format</Form.Label>
              <Form.Select
                value={savedDateFormat ?? ""}
                onChange={(e) => setField("preferred_date_format", e.target.value || null)}
                disabled={dateFormats.isLoading || dateFormats.failed}
              >
                <option value="">
                  {defaultDateFormat ? `Not set (default: ${today.toFormat(defaultDateFormat.pattern)})` : "Not set"}
                </option>
                {dateFormats.formats.map((format) => (
                  <option key={format.code} value={format.code}>
                    {today.toFormat(format.pattern)}
                  </option>
                ))}
                {unlistedDateFormat && <option value={unlistedDateFormat}>{unlistedDateFormat}</option>}
              </Form.Select>
              {dateFormats.failed && (
                <Form.Text className="text-muted">Date formats couldn't be loaded. Reload the page to try again.</Form.Text>
              )}
            </Form.Group>
          </Col>
          {YES_NO_FIELDS.map(({ field, label }) => (
            <Col md={6} key={field}>
              <Form.Group className="mb-3" controlId={`brand-${field}`}>
                <Form.Label className="fw-semibold">{label}</Form.Label>
                <Form.Select
                  value={yesNo(brandForm[field])}
                  onChange={(e) => setField(field, e.target.value === "" ? null : e.target.value === "true")}
                >
                  <option value="">Not set</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </Form.Group>
            </Col>
          ))}
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="brand-key-terms">
              <Form.Label className="fw-semibold">Key Terms</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={brandForm.key_terms}
                onChange={(e) => setField("key_terms", e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="brand-notes">
              <Form.Label className="fw-semibold">Brand Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={brandForm.brand_notes}
                onChange={(e) => setField("brand_notes", e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <div className="d-flex justify-content-end">
          <Button variant="primary" size="sm" onClick={() => onSaveSection(EDITORIAL_FIELDS)} disabled={isLoading}>
            Save Editorial Guidelines
          </Button>
        </div>
      </div>
    ),
    AD_TOKEN_VALUES: (
      <BrandAdTokenValuesSection
        values={adTokenValues}
        setValues={setAdTokenValues}
        availableAdTokens={availableAdTokens}
        onSave={onSaveAdTokenValues}
        isLoading={isLoading}
      />
    ),
  };

  return (
    <div>
      {isLoading && <Loader />}

      <div className="mb-4">
        <h5 className="mb-1">Brand Identity</h5>
        <p className="text-muted small mb-0">Customize your organization's visual identity</p>
      </div>

      {brandIdentityV2 && missingFields.length > 0 && (
        <div className="alert alert-warning small" role="status" aria-label="Brand incomplete">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <span className="fw-semibold">Brand incomplete.</span> Missing: {missingFields.join(", ")}
        </div>
      )}

      <Form>
        {brandIdentityV2 ? (
          V2_SECTIONS.map((section) => <Fragment key={section}>{v2Sections[section]}</Fragment>)
        ) : (
          <>
            <div className="brand-section">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Company Website</Form.Label>
                    <Form.Control
                      type="url"
                      value={brandForm.company_site_url}
                      onChange={(e) => {
                        setBrandForm((prev: any) => ({
                          ...prev,
                          company_site_url: e.target.value,
                        }));
                      }}
                      placeholder="https://company.com"
                    />
                  </Form.Group>
                </Col>

              </Row>
            </div>

            <div className="brand-section">
              <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                <div>
                  <h6 className="brand-section-title">Logos</h6>
                  <p className="brand-section-hint">
                    Every variant on file for this brand. The default is what Create applies when rebranding ads —
                    select any variant to make it the default.
                  </p>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <span className="brand-section-count">
                    {tiles.length} {tiles.length === 1 ? "variant" : "variants"}
                  </span>
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    className="d-none"
                    onChange={(e) => handleAssetFile(e)}
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`btn btn-sm btn-outline-primary mb-0 text-nowrap ${isLoading ? "disabled" : ""}`}
                    title="Adds a variant. The default only changes if this brand has none."
                  >
                    <i className="bi bi-upload me-2"></i>
                    Upload New
                  </label>
                </div>
              </div>

              {tiles.length > 0 ? (
                <div className="brand-tile-grid">{tiles.map(renderTile)}</div>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-image fs-3 d-block mb-2"></i>
                  <div className="small">No logos on file yet</div>
                </div>
              )}

              <div className="text-muted small mt-3">Supported formats: JPG, PNG, SVG</div>
            </div>

            <div className="brand-section">
              <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                <div>
                  <h6 className="brand-section-title">Colors</h6>
                  <p className="brand-section-hint">
                    Selectable in Create for text, shapes and backgrounds.
                  </p>
                </div>
                <span className="brand-section-count">{COLOR_ROLES.length} colors</span>
              </div>

              <div className="brand-swatch-grid">
                {COLOR_ROLES.map(({ field, label }) => (
                  <div key={field} className="brand-swatch">
                    <div className="brand-swatch-well">
                      <Form.Control
                        type="color"
                        aria-label={`${label} color`}
                        value={brandForm[field] || "#000000"}
                        onChange={(e) => setColor(field, e.target.value)}
                      />
                    </div>
                    <div className="brand-swatch-body">
                      <Form.Select
                        className="brand-swatch-role mb-1"
                        aria-label={`Role for ${brandForm[field]}`}
                        value={field}
                        onChange={(e) => assignRole(field, e.target.value as ColorField)}
                      >
                        {COLOR_ROLES.map((role) => (
                          <option key={role.field} value={role.field}>
                            {role.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control
                        type="text"
                        className="brand-swatch-hex"
                        value={brandForm[field]}
                        onChange={(e) => setColor(field, e.target.value)}
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {fontsSection}

            {/* Only with brand-identity-v2 off. With it on, every input is in a section with its
                own Save, and fonts save at once; this button saved none of those, so a click read
                as "saved" and left the section edits unsaved. */}
            <div className="d-flex justify-content-end pt-3 mt-4 border-top">
              <Button variant="primary" onClick={update} className="px-4" disabled={isLoading}>
                <i className="bi bi-check-circle me-2"></i>
                Save Changes
              </Button>
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

export default OrganizationBrandTab;
